import { map } from 'lodash';
import { requestCascoPrices, requestPrice } from '../../api/cmtplCalcApi';
import { searchVehicle } from '../../api/cmtplContractApi';
import {
  CASCO_COVERAGES,
  DURATION_TYPES,
  CMTPL_CASCO_EMPTY_AMOUNTS,
  CMTPL_EMPTY_PRICES,
  MONTHS,
  OWN_USE_TYPE,
  CMTPL_CASCO_EMPTY_DISCOUNTS,
  DEFAULT_SEAT_COUNT,
} from '../../constants/cmtplCalcConstants';
import { calculateEndDateByMonths } from '../../helpers/cmtpl/cmtplContractHelpers';
import { PAYMENT_FREQUENCIES, SINGLE } from '../../constants/paymentFrequency';
import { getArmenianStartDate, convertToArm } from '../../helpers/dates';

const SELECT_COVERAGES = 'efes/cmtplCalc/SELECT_COVERAGES';
const SELECT_SEAT_COUNT = 'efes/cmtplCalc/SELECT_SEAT_COUNT';
const SET_INVALID_SEAT_COUNT = 'efes/cmtplCalc/SET_INVALID_SEAT_COUNT';

const GET_PRICES = 'efes/cmtplCalc/GET_PRICES';
const GET_PRICES_SUCCESS = 'efes/cmtplCalc/GET_PRICES_SUCCESS';
const GET_PRICES_FAIL = 'efes/cmtplCalc/GET_PRICES_FAIL';

const GET_CASCO_PRICES = 'efes/cmtplCalc/GET_CASCO_PRICES';
const GET_CASCO_PRICES_SUCCESS = 'efes/cmtplCalc/GET_CASCO_PRICES_SUCCESS';
const GET_CASCO_PRICES_FAIL = 'efes/cmtplCalc/GET_CASCO_PRICES_FAIL';

const SEARCH_DRAFT_DATA = 'efes/cmtplCalc/SEARCH_DRAFT_DATA';
const SEARCH_DRAFT_DATA_SUCCESS = 'efes/cmtplCalc/SEARCH_DRAFT_DATA_SUCCESS';
const SEARCH_DRAFT_DATA_FAIL = 'efes/cmtplCalc/SEARCH_DRAFT_DATA_FAIL';

const SET_CALC_DATA = 'efes/cmtplCalc/SET_CALC_DATA';

const CLEAR_CMTPL_PRICES = 'efes/cmtplCalc/CLEAR_CMTPL_PRICES';
const CLEAR_CASCO_PRICES = 'efes/cmtplCalc/CLEAR_CASCO_PRICES';

const SET_PAYMENT_FREQUENCY = 'efes/cmtplCalc/SET_PAYMENT_FREQUENCY';
const CLEAR_PAYMENT_FREQUENCY = 'efes/cmtplCalc/CLEAR_PAYMENT_FREQUENCY';

const initialState = {
  prices: CMTPL_EMPTY_PRICES,
  paymentFrequencies: {},
  paymentsSchedule: { standard: {} },
  paymentFrequency: PAYMENT_FREQUENCIES[SINGLE],
  cascoAmounts: CMTPL_CASCO_EMPTY_AMOUNTS,
  cascoDiscounts: CMTPL_CASCO_EMPTY_DISCOUNTS,
  selectedCoverages: CASCO_COVERAGES,
  selectedSeatCount: DEFAULT_SEAT_COUNT,
  isInvalidSeatCount: true,
  isFetchingCascoPrice: false,
  cascoPriceError: false,
  isFetchingPrice: false,
  isFetchingDraft: false,
  priceError: null,
  idNumber: '',
  plateNumber: '',
  suggestedStartDate: null,
  useType: OWN_USE_TYPE,
  duration: MONTHS.MAX,
  durationType: DURATION_TYPES.MONTH,
  startDate: getArmenianStartDate(),
  endDate: null,
  contractDays: 0,
};

function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SELECT_COVERAGES:
      return {
        ...state,
        selectedCoverages: action.data,
      };
    case SELECT_SEAT_COUNT:
      return {
        ...state,
        selectedSeatCount: action.data,
      };
    case SET_INVALID_SEAT_COUNT:
      return {
        ...state,
        isInvalidSeatCount: action.data,
      };
    case GET_PRICES:
      return {
        ...state,
        isFetchingPrice: true,
      };
    case GET_PRICES_SUCCESS:
      return {
        ...state,
        isFetchingPrice: false,
        prices: {
          ...state.prices,
          ...action.result,
        },
      };
    case GET_PRICES_FAIL:
      return {
        ...state,
        isFetchingPrice: false,
        priceError: action.error,
      };
    case GET_CASCO_PRICES:
      return {
        ...state,
        isFetchingCascoPrice: true,
      };
    case GET_CASCO_PRICES_SUCCESS: {
      const { paymentsType, amounts, discounts, paymentsSchedule } = action.result;
      return {
        ...state,
        isFetchingCascoPrice: false,
        paymentFrequencies: { standard: paymentsType },
        paymentsSchedule: { standard: paymentsSchedule },
        cascoAmounts: { standard: amounts },
        cascoDiscounts: { standard: discounts },
      };
    }
    case GET_CASCO_PRICES_FAIL:
      return {
        ...state,
        isFetchingCascoPrice: false,
        cascoPriceError: action.error,
      };
    case SEARCH_DRAFT_DATA:
      return {
        ...state,
        isFetchingDraft: true,
      };
    case SEARCH_DRAFT_DATA_SUCCESS:
      return {
        ...state,
        isFetchingDraft: false,
      };
    case SEARCH_DRAFT_DATA_FAIL:
      return {
        ...state,
        isFetchingDraft: false,
      };

    case SET_CALC_DATA:
      return {
        ...state,
        ...action.data,
      };
    case CLEAR_CMTPL_PRICES:
      return {
        ...state,
        prices: {
          main: null,
          twelveMonths: null,
          sixMonths: null,
          threeMonths: null,
        },
      };
    case CLEAR_CASCO_PRICES:
      return {
        ...state,
        paymentFrequencies: {},
        paymentsSchedule: { standard: {} },
        cascoAmounts: CMTPL_CASCO_EMPTY_AMOUNTS,
        cascoDiscounts: CMTPL_CASCO_EMPTY_DISCOUNTS,
      };
    case SET_PAYMENT_FREQUENCY:
      return {
        ...state,
        paymentFrequency: action.data,
      };
    case CLEAR_PAYMENT_FREQUENCY:
      return {
        ...state,
        paymentFrequency: PAYMENT_FREQUENCIES[SINGLE],
      };
    default:
      return state;
  }
}

const createReducerWithName = (name) => (state = initialState, action) => {
  if (action.name !== name) {
    return state;
  }
  return reducer(state, action);
};

export const cmtplCalculatorReducerForContract = createReducerWithName('contract');
const cmtplCalculatorReducerForCalculatorPage = createReducerWithName('calculator');

export { cmtplCalculatorReducerForCalculatorPage as default };

export const selectCoverages = (coverages, name) => ({
  type: SELECT_COVERAGES,
  data: coverages,
  name,
});

export const selectSeatCount = (seat, name) => ({
  type: SELECT_SEAT_COUNT,
  data: seat,
  name,
});

export const setInvalidSeatCount = (invalidSeatCount, name) => ({
  type: SET_INVALID_SEAT_COUNT,
  data: invalidSeatCount,
  name,
});

const getRequestDates = (start) => {
  const endDate1 = convertToArm(calculateEndDateByMonths(start, 3));
  const endDate2 = convertToArm(calculateEndDateByMonths(start, 6));
  const endDate3 = convertToArm(calculateEndDateByMonths(start, 12));

  return [
    null,
    {
      startDate: start,
      endDate: endDate1,
      startTimestamp: start.getTime(),
      endTimestamp: endDate1.getTime(),
    },
    {
      startDate: start,
      endDate: endDate2,
      startTimestamp: start.getTime(),
      endTimestamp: endDate2.getTime(),

    },
    {
      startDate: start,
      endDate: endDate3,
      startTimestamp: start.getTime(),
      endTimestamp: endDate3.getTime(),
    },
  ];
};

export function requestAllPrices(data, name) {
  const periods = getRequestDates(data.startDate);
  const priceRequests = map(periods, (period) => {
    const params = period ? { ...data, ...period } : data;
    return requestPrice(params);
  });

  return {
    actions: [
      { type: GET_PRICES, name },
      { type: GET_PRICES_SUCCESS, name },
      { type: GET_PRICES_FAIL, name, showError: true, customMsg: 'getPricesFail' },
    ],
    promise: Promise.all(priceRequests).then(([a, b, c, d]) => ({
      main: a,
      threeMonths: b,
      sixMonths: c,
      twelveMonths: d,
    })),
  };
}

export function getCascoPrices(payload, name) {
  return {
    actions: [
      { type: GET_CASCO_PRICES, name },
      { type: GET_CASCO_PRICES_SUCCESS, name },
      { type: GET_CASCO_PRICES_FAIL, name, showError: true, customMsg: 'getPricesFail' },
    ],
    promise: requestCascoPrices(payload).then((data) => data.result),
  };
}

export const setCalcData = (data, name) => ({
  type: SET_CALC_DATA,
  data,
  name,
});

export const clearCmtplPrices = (name) => ({
  type: CLEAR_CMTPL_PRICES,
  name,
});

export const clearCascoPrices = (name) => ({
  type: CLEAR_CASCO_PRICES,
  name,
});

export const getCalcDraftData = (data, name) => (dispatch) => dispatch({
  actions: [
    { type: SEARCH_DRAFT_DATA, name },
    { type: SEARCH_DRAFT_DATA_SUCCESS, name },
    {
      type: SEARCH_DRAFT_DATA_FAIL,
      name,
      showError: true,
    },
  ],
  promise: searchVehicle(data),
});

export const setPaymentFrequency = (data, name) => ({
  type: SET_PAYMENT_FREQUENCY,
  data,
  name,
});

export const clearPaymentFrequency = (name) => ({
  type: CLEAR_PAYMENT_FREQUENCY,
  name,
});
